import { ChangeDetectionStrategy, Component, DestroyRef, effect, forwardRef, inject, input, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, filter, of, switchMap, tap } from 'rxjs';
import { ComponentsModule } from '../../../../../lib';
import { SlotComponent } from '../slot.component';
import { TranslationKeyPipe, SlotTooltipPipe } from '../../../pipes';
import { VisualComponent } from '../../visual/visual.component';
import { CitiesItem, CitiesService, INITIAL_QUESTION_FLOW_PROJECT_DATA } from '../../../../v-api';
import { ContextModelService } from '../../../services';
import { TemplateSlot } from '../../../interfaces';

const DEFAULT_COUNTRY_CODE = INITIAL_QUESTION_FLOW_PROJECT_DATA.countryCode;
const MIN_SEARCH_LENGTH = 1;
const DEBOUNCE_MS = 300;

@Component({
    selector: 'ca-slot-address-autocomplete-input',
    imports: [ComponentsModule, ReactiveFormsModule, MatAutocompleteModule, TranslationKeyPipe, SlotTooltipPipe, VisualComponent],
    templateUrl: './slot-address-autocomplete-input.component.html',
    styleUrl: './slot-address-autocomplete-input.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SlotAddressAutocompleteInputComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SlotAddressAutocompleteInputComponent extends SlotComponent implements OnInit {
    public cities: CitiesItem[] = [];
    public loading = false;
    public isOpened: boolean = false;
    public citySlot = input.required<TemplateSlot>();
    private citiesService = inject(CitiesService);
    private contextModel = inject(ContextModelService);
    private destroyRef = inject(DestroyRef);

    override ngOnInit() {
        super.ngOnInit();
        this.initAutocomplete();
    }

    private readonly valueEffect = effect(() => {
        if (this.control.value) return;
        const displayValue = this.formatValueToDisplayValue(this.value());
        this.control.setValue(displayValue, { emitEvent: false });
    });

    override writeValue(value: any): void {
        this.value.set(value);
    }

    onOptionSelected(event: MatAutocompleteSelectedEvent): void {
        if (!this.isDisabled) {
            const city = event.option.value as CitiesItem;
            const displayValue = this.formatCity(city);
            this.control.setValue(displayValue, { emitEvent: false });
            const formattedValue = this.formatSelectedOptionToValue(city);
            this.value.set(formattedValue);
            this.onChange(formattedValue);
        }
    }

    formatValueToDisplayValue(value: any): any {
        if (!value || !value[this.slot().id]) return;

        return value[this.citySlot().id] ? `${value[this.slot().id]} ${value[this.citySlot().id]}` : value[this.slot().id];
    }

    onInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.control.setValue(value, { emitEvent: false });
        this.value.set(null);
        this.onChange(null);
    }

    formatSelectedOptionToValue(selectedOption: CitiesItem): any {
        const map = {
            [this.slot().id]: selectedOption.postalCode,
            [this.citySlot().id]: selectedOption.city || ''
        };
        return map;
    }

    formatCity(city: CitiesItem): string {
        return city.city ? `${city.postalCode} ${city.city}` : city.postalCode;
    }

    private initAutocomplete(): void {
        this.control.valueChanges
            .pipe(
                filter(value => typeof value === 'string' && value.length >= MIN_SEARCH_LENGTH),
                debounceTime(DEBOUNCE_MS),
                distinctUntilChanged(),
                tap(() => {
                    this.loading = true;
                }),
                switchMap(searchString => {
                    const countryCode = this.getCountryCode();
                    return this.citiesService.getCities(countryCode, searchString).pipe(catchError(() => of({ cities: [] })));
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(response => {
                this.cities = response.cities || [];
                this.loading = false;
                this.cdr.detectChanges();
            });
    }

    private getCountryCode(): string {
        const state = this.contextModel.currentState;
        return state?.project?.address?.countryCode || DEFAULT_COUNTRY_CODE;
    }
}
